import React, { useEffect, useState } from "react";
import { Avatar, message } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import Logout from "./Logout";
import axios from "../../utils/axios";
import { Coins } from "lucide-react";

const PROJECT_STEPS = [
  { key: "categorySelected", label: "Category Selected", weight: 8 },
  { key: "cameraModelSelected", label: "Camera Selected", weight: 7 },
  { key: "cameraAdded", label: "Camera Added", weight: 8 },
  { key: "uploaded", label: "Upload Images", weight: 8 },
  { key: "annotated", label: "Annotation", weight: 10 },
  { key: "dataset", label: "Dataset Ready", weight: 7 },
  { key: "versioned", label: "Version Created", weight: 5 },
  { key: "trained", label: "Model Trained", weight: 10 },
  { key: "cameraModelConfigured", label: "Camera Model Configured", weight: 12 },
  { key: "dataflowConfigured", label: "Dataflow Pipeline Configured", weight: 15 },
  { key: "dashboardConfigured", label: "Dashboard Configured", weight: 10 },
];

export default function CustomHeader({ auth }) {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(0);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [progressLoading, setProgressLoading] = useState(false);
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem("user"));

  const hideProjectInHeader =
    location.pathname.toLowerCase() === "/create-project" ||
    location.pathname.toLowerCase() === "/credits";

  const userName = auth?.user
    ? auth.user[0].toUpperCase() + auth.user.slice(1)
    : "User";

  const organizationId = localStorage.getItem("organizationId");
  const [projectName, setProjectName] = useState(null);

  useEffect(() => {
    const storedProjectName = localStorage.getItem("projectname");
    setProjectName(storedProjectName || null);
  }, []);

  const hasSelectedProject = !!projectName;

  useEffect(() => {
    if (auth?.user && organizationId) fetchCredits();
  }, []);

  const fetchCredits = async () => {
      try {
        setCreditsLoading(true);
        const response = await axios.post("/fetch-credits", {
          username: auth?.user,
          organizationId,
        });
        if (response.data.success) setCredits(response.data.credits);
        setCreditsLoading(false)
      } catch (err) {
        setCreditsLoading(false)
        if (err.response && err.response.data) {
          message.warning(err.response.data.msg ? err.response.data.msg : err.response.data);
        } else if (err.response && err.response.data && err.response.data.userName) {
          message.warning(err.response.data.userName[0]);
        } else if (err.response && err.response.status === 401) {
          message.error("User not authorised");
        } else {
          message.error("Something went wrong");
        }
      }
    };

  const initials = auth?.user ? auth.user.slice(0, 2).toUpperCase() : "AD";

  useEffect(() => {
    const fetchProjectProgress = async () => {
      if (!projectName) return;
      try {
        setProgressLoading(true);
        const res = await axios.post("/get-project-summary", {
          projectname: projectName,
          organizationId,
        });
        const status = res.data.status;
        let completed = 0;
        PROJECT_STEPS.forEach((step) => {
          if (status[step.key]) completed += step.weight;
        });
        setProgress(completed);
      } catch (err) {
        console.error("Header workflow fetch failed", err);
        setProgress(0);
      } finally {
        setProgressLoading(false);
      }
    };
    fetchProjectProgress();
  }, [projectName, organizationId]);

  const goToCredits = () => navigate("/credits", { state: { tab: "view" } });

  const progressTextColor =
    progress === 100
      ? "text-green-600"
      : progress >= 50
      ? "text-blue-600"
      : "text-orange-500";

  const progressBarLeft =
    progress < 10 ? "2%" : progress > 90 ? "90%" : `${progress}%`;

  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">

      {/* ── MAIN ROW ──────────────────────────────────────────────── */}
      <div className="max-w-[1440px] mx-auto px-5 h-16 flex items-center justify-between gap-4">

        {/* LEFT: Logo + Title */}
        <div className="flex items-center gap-3.5 flex-shrink-0">
          <img
            src="/citriot_logo.webp"
            alt="Citriot"
            className="h-9 w-auto object-contain"
          />
          <div className="w-px h-7 bg-slate-200 flex-shrink-0" />
          <span className="text-[15px] font-bold text-[#1A59A7] tracking-tight whitespace-nowrap hidden sm:block">
            Admin Dashboard
          </span>
        </div>

        {/* CENTER: Project Badge */}
        <div className="flex-1 flex justify-center min-w-0">
          {hasSelectedProject && !hideProjectInHeader && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full max-w-xs min-w-0">
              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
              <span className="max-w-[160px] truncate text-xs font-semibold text-slate-700">
                {projectName}
              </span>
              <span className="h-4 w-px bg-slate-300 flex-shrink-0" />
              {progressLoading ? (
                <span className="text-xs text-slate-400 flex-shrink-0">--%</span>
              ) : (
                <span className={`text-xs font-bold flex-shrink-0 ${progressTextColor}`}>
                  {progress}%
                </span>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Plan + Credits + User + Logout */}
        <div className="flex items-center gap-2.5 flex-shrink-0">

          {/* Plan Button */}
          {user?.subscribe ? (
            <button
              onClick={() => navigate("/subscription")}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12.5px] font-bold
                bg-gradient-to-br from-green-50 to-green-100 text-green-700 border border-green-200
                shadow-sm hover:shadow-md hover:from-green-100 hover:to-green-200
                transition-all duration-200 hover:-translate-y-px whitespace-nowrap cursor-pointer"
            >
              {/* Pulsing dot */}
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-50" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="hidden sm:inline">Active Plan</span>
              {/* Crown */}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" className="opacity-70 flex-shrink-0">
                <path d="M2 20h20v2H2v-2zM4 17l4-8 4 4 4-6 4 10H4z" />
              </svg>
            </button>
          ) : (
            <button
              onClick={() => navigate("/subscription")}
              className="relative flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12.5px] font-bold
                bg-gradient-to-br from-blue-700 to-indigo-600 text-white overflow-hidden
                shadow-[0_2px_10px_rgba(79,70,229,0.35)]
                hover:shadow-[0_4px_18px_rgba(79,70,229,0.48)] hover:-translate-y-px
                active:translate-y-0 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-full pointer-events-none" />
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="flex-shrink-0 relative z-10">
                <path d="M13 2L4.5 13H11L10 22L20.5 11H14L13 2Z" />
              </svg>
              <span className="hidden sm:inline relative z-10">Upgrade</span>
              <svg
                width="11" height="11" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                className="flex-shrink-0 opacity-80 relative z-10"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Credits */}
            <div className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full cursor-pointer
              bg-gradient-to-br from-green-50 to-green-100 border border-green-200
              hover:shadow-[0_2px_10px_rgba(34,197,94,0.15)] hover:-translate-y-px
              transition-all duration-200" onClick={goToCredits}>
              <Coins size={14} color="#16a34a" strokeWidth={2.5} />
              {creditsLoading ? (
                <div className="w-12 h-3.5 rounded bg-green-200 animate-pulse" />
              ) : (
                <>
                  <span className="text-[13px] font-bold text-green-700 tracking-tight">
                    {credits || 0}
                  </span>
                  <span className="text-[11px] font-medium text-green-400 hidden sm:inline">
                    credits
                  </span>
                </>
              )}
            </div>

          {/* User chip */}
          <div className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 transition-all duration-200">
            <Avatar
              size={32}
              icon={<UserOutlined />}
              className="!bg-gradient-to-br !from-blue-500 !to-indigo-500 !border-2 !border-white flex-shrink-0 !text-[12px] !font-bold"
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}
            >
              {initials}
            </Avatar>
            <span className="text-[13px] font-semibold text-slate-700 whitespace-nowrap hidden sm:inline">
              {userName}
            </span>
          </div>

          {/* Logout */}
          <div className="w-9 h-9 rounded-xl flex items-center justify-center border border-transparent bg-transparent cursor-pointer text-slate-500 hover:text-slate-700 hover:bg-slate-100 hover:border-slate-200 transition-all duration-200">
            <Logout configure={true} />
          </div>

        </div>
      </div>

      {/* ── PROJECT PROGRESS BAR ──────────────────────────────────── */}
      {hasSelectedProject && !hideProjectInHeader && (
        <div className="relative w-full bg-slate-100 h-1.5 overflow-hidden">
          <div
            className={`h-1.5 transition-all duration-500 ease-out ${
              progress === 100
                ? "bg-gradient-to-r from-green-600 to-green-400"
                : "bg-gradient-to-r from-green-500 to-green-300"
            }`}
            style={{ width: `${progress}%` }}
          />
          <span
            className="absolute top-[-18px] text-[11px] font-semibold text-green-700 transition-all duration-500 -translate-x-1/2"
            style={{ left: progressBarLeft }}
          >
            {progress}%
          </span>
        </div>
      )}
    </header>
  );
}